import type { AxiosResponse } from 'axios'
import type {
  AiGenerateBo,
  AiResponseVo,
  AiSumUsageVo,
  AiUsageMonthVo,
  AiUsageQuery,
  AiUsageRecordVo,
  AudioMessageVo,
  ChatConversationVo,
  ChatMessageVo,
  ImageMessageVo,
  ModelBo,
  ModelVo,
  ProviderModelBo,
  ProviderModelVo,
  UserCreationShareBo,
  UserCreationShareVo,
  UserTransactionQuery,
  UserTransactionVo,
  VideoMessageVo,
} from '@/api/ai/types.ts'
import api from '@/utils/axios.ts'

/**
 * AI通用生成接口 包含对话、生成图片、生成视频
 */
export function aiGenerateApi(data: AiGenerateBo): Promise<AxiosResponse<AiResponseVo>> {
  return api({
    url: '/ai/generate',
    method: 'post',
    data,
  })
}

/**
 * AI文本生成接口（新版）
 */
export function aiGenerateV1Api(data: AiGenerateBo): Promise<AxiosResponse<AiResponseVo>> {
  return api({
    url: '/ai/generate',
    method: 'post',
    data,
  })
}

/**
 * 查询异步生成任务状态。响应字段由不同模型供应商透传，调用方统一归一化处理。
 */
export function aiGenerationTaskApi(taskId: string): Promise<any> {
  return api({
    url: `/ai/generation/${encodeURIComponent(taskId)}`,
    method: 'get',
  })
}

/** 查询当前登录用户仍在生成中的任务。 */
export function aiActiveGenerationTasksApi(): Promise<any> {
  return api({
    url: '/ai/task/active',
    method: 'get',
  })
}

/** 标记生成任务的完成或失败结果已经被用户查看。 */
export function aiAcknowledgeGenerationTaskApi(taskId: string): Promise<any> {
  return api({
    url: `/ai/task/${encodeURIComponent(taskId)}/acknowledge`,
    method: 'post',
  })
}

/**
 * 对话管理-对话窗口分页列表
 */
export function chatConversationPageApi(data: any): Promise<AxiosResponse<PageResult<ChatConversationVo>>> {
  return api({
    url: '/api/chat/conversation/page',
    method: 'post',
    data,
  })
}

/**
 * 对话管理-详情
 */
export function chatConversationDetailApi(conversationId: string): Promise<AxiosResponse<ChatConversationVo>> {
  return api({
    url: `/ai/chat/conversation/detail/${conversationId}`,
    method: 'post',
  })
}

/**
 * 对话管理-修改对话窗口
 */
export function chatConversationUpdateApi(conversationId: string, title: string): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/chat/conversation/update',
    method: 'post',
    data: {
      id: conversationId,
      title,
    },
  })
}

/**
 * 对话管理-修改对话窗口
 */
export function chatConversationDeleteApi(conversationId: string): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/chat/conversation/delete',
    method: 'post',
    data: [conversationId],
  })
}

/**
 * 对话管理-清除对话窗口
 */
export function chatConversationClearApi() {
  return api({
    url: '/ai/chat/conversation/clear',
    method: 'post',
  })
}

/**
 * 对话管理-对话消息分页列表
 */
export function chatMessagePageApi(data: any): Promise<AxiosResponse<PageResult<ChatMessageVo>>> {
  return api({
    url: '/api/chat/message/page',
    method: 'post',
    data,
  })
}

/**
 * 绘画管理-分页列表
 */
export function imageMessagePageApi(data: any, signal?: AbortSignal): Promise<AxiosResponse<PageResult<ImageMessageVo>>> {
  return api({
    url: '/api/image/message/page',
    method: 'post',
    data,
    signal,
  })
}

/**
 * 视频管理-分页列表
 */
export function videoMessagePageApi(data: any, signal?: AbortSignal): Promise<AxiosResponse<PageResult<VideoMessageVo>>> {
  return api({
    url: '/api/video/message/page',
    method: 'post',
    data,
    signal,
  })
}

/**
 * 音频管理-分页列表
 */
export function audioMessagePageApi(data: any, signal?: AbortSignal): Promise<AxiosResponse<PageResult<AudioMessageVo>>> {
  return api({
    url: '/api/audio/message/page',
    method: 'post',
    data,
    signal,
  })
}

/**
 * AI中心管理-用量汇总
 */
export function aiSumUsageApi(): Promise<AxiosResponse<AiSumUsageVo>> {
  return api({
    url: '/ai/model/usage/summary',
    method: 'post',
  })
}

/**
 * AI中心管理-按月统计使用量
 */
export function aiUsageByMonthApi(date: string): Promise<AxiosResponse<AiUsageMonthVo>> {
  return api({
    url: '/ai/model/usage/month',
    method: 'post',
    data: {
      date,
    },
  })
}

/**
 * AI中心管理-算力用量记录分页
 */
export function aiUsagePageApi(data: PageQuery<AiUsageQuery>): Promise<AxiosResponse<PageResult<AiUsageRecordVo>>> {
  return api({
    url: '/ai/model/usage/page',
    method: 'post',
    data,
  })
}

/**
 * AI中心管理-积分记录分页
 */
export function aiUserTransactionPageApi(data: PageQuery<UserTransactionQuery>): Promise<AxiosResponse<PageResult<UserTransactionVo>>> {
  return api({
    url: '/user/transaction/page',
    method: 'post',
    data,
  })
}

/**
 * 图片消息删除
 */
export function imageMessageDeleteApi(ids: string[]): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/image/message/delete',
    method: 'post',
    data: ids,
  })
}

/**
 * 视频消息删除
 */
export function videoMessageDeleteApi(ids: string[]): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/video/message/delete',
    method: 'post',
    data: ids,
  })
}

/**
 * 音频消息删除
 */
export function audioMessageDeleteApi(ids: string[]): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/audio/message/delete',
    method: 'post',
    data: ids,
  })
}

/**
 * 图片消息删除（新接口）
 */
export function imageMessageDeletedApi(ids: string[]): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/image/message/deleted',
    method: 'post',
    data: ids,
  })
}

/**
 * 视频消息删除（新接口）
 */
export function videoMessageDeletedApi(ids: string[]): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/video/message/deleted',
    method: 'post',
    data: ids,
  })
}

/**
 * 音频消息删除（新接口）
 */
export function audioMessageDeletedApi(ids: string[]): Promise<AxiosResponse<boolean>> {
  return api({
    url: '/ai/audio/message/deleted',
    method: 'post',
    data: ids,
  })
}

/**
 * 发布灵感创作分享
 */
export function aiCreationPublishApi(data: UserCreationShareBo): Promise<AxiosResponse<string>> {
  return api({
    url: '/user/creation/share/publish',
    method: 'post',
    data,
  })
}

/**
 * 创作灵感分享-分页列表
 */
export function aiCreationSharePageApi(data: any): Promise<AxiosResponse<PageResult<UserCreationShareVo>>> {
  return api({
    url: '/api/creation/share/page',
    method: 'post',
    data,
  })
}

/**
 * 模型广场-模型分页列表
 */
export function aiModelPageApi(data: PageQuery<ModelBo>): Promise<AxiosResponse<PageResult<ModelVo>>> {
  return api({
    url: '/api/model/page',
    method: 'post',
    data,
  })
}

/**
 * 模型广场-供应商模型分页列表
 */
export function aiProviderModelPageApi(data: PageQuery<ProviderModelBo>): Promise<AxiosResponse<PageResult<ProviderModelVo>>> {
  return api({
    url: '/api/provider/model/page',
    method: 'post',
    data,
  })
}
